import { isGlobalViewer } from '../../../constants/roles';
// src/modules/treasury/pages/TreasurySettings.jsx
// HO-only treasury settings hub.

import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Typography, Spin, Alert, Tabs, Form, InputNumber,
  Button, Switch, Space, Tag, message, Row, Col,
  Tooltip, Popconfirm, Collapse, Divider, Badge, Select,
} from 'antd';
import {
  SettingOutlined, BranchesOutlined, LockOutlined, SaveOutlined,
  EditOutlined, CloseOutlined, CheckOutlined, BankOutlined,
  CalendarOutlined, SafetyCertificateOutlined, DollarOutlined,
  ShopOutlined, EyeInvisibleOutlined, UnlockOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../../context/AuthContext';
import { fetchBranchById, fetchBranches } from '../../branches/api';
import { fetchUsers } from '../../users/api';
import {
  fetchTreasurySettings,
  updateTreasurySettings,
  fetchBranchOverrides,
  setBranchBypassDayClosure,
  setBranchBypassPastDateEod,
  setBranchBankAutoApprove,
  setBranchCashLimit,
  setBranchPledgeCashLimit,
  setBranchHideExpectedClosingCash,
  setBranchHideExpectedCash,
  setBranchHideDifference,
  setBranchHideGstToggle,
  setBranchHideProcessingFeeToggle,
} from '../api';

const { Title, Text, Paragraph } = Typography;

const fmtAmount = (v) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v);

// ── Reusable setting row ───────────────────────────────────────────────────────
function SettingRow({ icon, label, description, control, divider = true }) {
  return (
    <>
      <Row align="middle" style={{ padding: '12px 0' }} gutter={16}>
        <Col flex="none" style={{ color: '#8c8c8c', fontSize: 18, lineHeight: 1 }}>
          {icon}
        </Col>
        <Col flex="auto">
          <Text strong style={{ fontSize: 13 }}>{label}</Text>
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>{description}</Text>
          </div>
        </Col>
        <Col flex="none">
          {control}
        </Col>
      </Row>
      {divider && <Divider style={{ margin: 0 }} />}
    </>
  );
}

export default function TreasurySettings() {
  const { user } = useAuth();

  const [isHO, setIsHO] = useState(false);
  const [initLoading, setInitLoading] = useState(true);
  const [messageApi, contextHolder] = message.useMessage();

  // ── General settings ──────────────────────────────────────────────────────
  const [generalForm] = Form.useForm();
  const [generalSaving, setGeneralSaving] = useState(false);
  const [generalLoading, setGeneralLoading] = useState(false);
  const [globalThreshold, setGlobalThreshold] = useState(20000);
  const [globalPledgeThreshold, setGlobalPledgeThreshold] = useState(0);
  const [globalGstToggle, setGlobalGstToggle] = useState(true);
  const [globalProcessingFeeToggle, setGlobalProcessingFeeToggle] = useState(true);

  // ── Day-closure override users ─────────────────────────────────────────────
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  // ── Branch overrides ──────────────────────────────────────────────────────
  const [branches, setBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);

  const [bypassedIds, setBypassedIds] = useState([]);
  const [bypassPastDateIds, setBypassPastDateIds] = useState([]);
  const [bankAutoApproveIds, setBankAutoApproveIds] = useState([]);
  const [cashApprovalLimits, setCashApprovalLimits] = useState({});
  const [pledgeCashApprovalLimits, setPledgeCashApprovalLimits] = useState({});

  const [hideExpectedClosingCashIds, setHideExpectedClosingCashIds] = useState([]);
  const [hideExpectedCashIds, setHideExpectedCashIds] = useState([]);
  const [hideDifferenceIds, setHideDifferenceIds] = useState([]);
  const [hideGstToggleIds, setHideGstToggleIds] = useState([]);
  const [hideProcessingFeeToggleIds, setHideProcessingFeeToggleIds] = useState([]);

  const [togglingId, setTogglingId] = useState(null);
  const [togglingPastDateId, setTogglingPastDateId] = useState(null);
  const [togglingBankId, setTogglingBankId] = useState(null);
  const [togglingHideECCId, setTogglingHideECCId] = useState(null);
  const [togglingHideECId, setTogglingHideECId] = useState(null);
  const [togglingHideDiffId, setTogglingHideDiffId] = useState(null);
  const [togglingHideGstToggleId, setTogglingHideGstToggleId] = useState(null);
  const [togglingHideProcessingFeeToggleId, setTogglingHideProcessingFeeToggleId] = useState(null);

  const [editingLimitId, setEditingLimitId] = useState(null);
  const [editingLimitValue, setEditingLimitValue] = useState(null);
  const [savingLimitId, setSavingLimitId] = useState(null);

  const [editingPledgeLimitId, setEditingPledgeLimitId] = useState(null);
  const [editingPledgeLimitValue, setEditingPledgeLimitValue] = useState(null);
  const [savingPledgeLimitId, setSavingPledgeLimitId] = useState(null);

  // ── Init ───────────────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        if (isGlobalViewer(user)) {
          setIsHO(true);
        } else if (user?.branch) {
          const branchRes = await fetchBranchById(user.branch);
          const branch = branchRes?.data || branchRes;
          setIsHO(branch?.branchType === 'HO');
        }
      } catch {
        // guard below handles denial
      } finally {
        setInitLoading(false);
      }
    })();
  }, [user]);

  // ── Load general settings ─────────────────────────────────────────────────
  const loadGeneralSettings = useCallback(async () => {
    setGeneralLoading(true);
    try {
      const res = await fetchTreasurySettings();
      const s = res?.data?.data || {};
      const threshold = s.cashApprovalThreshold ?? 20000;
      const pledgeThreshold = s.pledgeCashApprovalThreshold ?? 0;
      const gstToggle = s.gstToggleEnabled ?? true;
      const processingFeeToggle = s.processingFeeToggleEnabled ?? true;
      generalForm.setFieldsValue({
        cashApprovalThreshold: threshold,
        pledgeCashApprovalThreshold: pledgeThreshold,
        gstToggleEnabled: gstToggle,
        processingFeeToggleEnabled: processingFeeToggle,
        billHideGrossWeight: s.billHideGrossWeight ?? false,
        billHidePurity: s.billHidePurity ?? false,
        billHideDD: s.billHideDD ?? false,
        billHideNetWeight: s.billHideNetWeight ?? false,
        dayClosureOverrideUsers: s.dayClosureOverrideUsers ?? [],
      });
      setGlobalThreshold(threshold);
      setGlobalPledgeThreshold(pledgeThreshold);
      setGlobalGstToggle(gstToggle);
      setGlobalProcessingFeeToggle(processingFeeToggle);
    } catch {
      generalForm.setFieldsValue({ cashApprovalThreshold: 20000, pledgeCashApprovalThreshold: 0, gstToggleEnabled: true, processingFeeToggleEnabled: true, billHideGrossWeight: false, billHidePurity: false, billHideDD: false, billHideNetWeight: false });
    } finally {
      setGeneralLoading(false);
    }
  }, [generalForm]);

  // ── Load branch overrides ─────────────────────────────────────────────────
  const loadBranchOverrides = useCallback(async () => {
    setBranchesLoading(true);
    try {
      const [overridesRes, branchesRes] = await Promise.all([
        fetchBranchOverrides(),
        fetchBranches({ limit: 200 }),
      ]);
      applyOverrides(overridesRes?.data?.data || {});
      const list = branchesRes?.data?.data || branchesRes?.data || [];
      setBranches(list.filter(b => b.branchType !== 'HO'));
    } catch {
      messageApi.error('Failed to load branch overrides');
    } finally {
      setBranchesLoading(false);
    }
  }, [messageApi]);

  function applyOverrides(data) {
    setBypassedIds(data.bypassDayClosureCheck || []);
    setBypassPastDateIds(data.bypassPastDateEodCheck || []);
    setBankAutoApproveIds(data.bankAutoApprove || []);
    setCashApprovalLimits(data.cashApprovalLimits || {});
    setPledgeCashApprovalLimits(data.pledgeCashApprovalLimits || {});
    setHideExpectedClosingCashIds(data.hideExpectedClosingCash || []);
    setHideExpectedCashIds(data.hideExpectedCash || []);
    setHideDifferenceIds(data.hideDifference || []);
    setHideGstToggleIds(data.hideGstToggle || []);
    setHideProcessingFeeToggleIds(data.hideProcessingFeeToggle || []);
  }

  // ── Load users for the override selector ──────────────────────────────────
  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const res = await fetchUsers({ page: 1, limit: 500 });
      const list = res?.data || res?.data?.data || [];
      setUsers(Array.isArray(list) ? list : []);
    } catch {
      // non-fatal — selector just shows ids
    } finally {
      setUsersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!initLoading && isHO) {
      loadGeneralSettings();
      loadBranchOverrides();
      loadUsers();
    }
  }, [initLoading, isHO]);

  // ── Save general ──────────────────────────────────────────────────────────
  const handleSaveGeneral = async (values) => {
    setGeneralSaving(true);
    try {
      await updateTreasurySettings({
        cashApprovalThreshold: values.cashApprovalThreshold,
        pledgeCashApprovalThreshold: values.pledgeCashApprovalThreshold,
        gstToggleEnabled: values.gstToggleEnabled,
        processingFeeToggleEnabled: values.processingFeeToggleEnabled,
        billHideGrossWeight: values.billHideGrossWeight,
        billHidePurity: values.billHidePurity,
        billHideDD: values.billHideDD,
        billHideNetWeight: values.billHideNetWeight,
        dayClosureOverrideUsers: values.dayClosureOverrideUsers || [],
      });
      setGlobalThreshold(values.cashApprovalThreshold);
      setGlobalPledgeThreshold(values.pledgeCashApprovalThreshold);
      setGlobalGstToggle(values.gstToggleEnabled);
      setGlobalProcessingFeeToggle(values.processingFeeToggleEnabled);
      messageApi.success('Settings saved');
    } catch (err) {
      messageApi.error(err?.response?.data?.message || 'Failed to save settings');
    } finally {
      setGeneralSaving(false);
    }
  };

  // ── Toggle handlers ───────────────────────────────────────────────────────
  const handleToggleBypass = async (branchId, checked) => {
    setTogglingId(branchId);
    try {
      const res = await setBranchBypassDayClosure(branchId, checked);
      applyOverrides(res?.data?.data || {});
      messageApi.success(`Day closure enforcement ${checked ? 'bypassed' : 'restored'}`);
    } catch (err) {
      messageApi.error(err?.response?.data?.message || 'Failed to update');
    } finally {
      setTogglingId(null);
    }
  };

  const handleTogglePastDateEod = async (branchId, checked) => {
    setTogglingPastDateId(branchId);
    try {
      const res = await setBranchBypassPastDateEod(branchId, checked);
      applyOverrides(res?.data?.data || {});
      messageApi.success(`Past-date EOD ${checked ? 'allowed' : 'restricted'}`);
    } catch (err) {
      messageApi.error(err?.response?.data?.message || 'Failed to update');
    } finally {
      setTogglingPastDateId(null);
    }
  };

  const handleToggleBankAutoApprove = async (branchId, checked) => {
    setTogglingBankId(branchId);
    try {
      const res = await setBranchBankAutoApprove(branchId, checked);
      applyOverrides(res?.data?.data || {});
      messageApi.success(`Bank auto-approve ${checked ? 'enabled' : 'disabled'}`);
    } catch (err) {
      messageApi.error(err?.response?.data?.message || 'Failed to update');
    } finally {
      setTogglingBankId(null);
    }
  };

  const handleToggleHideECC = async (branchId, hide) => {
    setTogglingHideECCId(branchId);
    try {
      const res = await setBranchHideExpectedClosingCash(branchId, hide);
      applyOverrides(res?.data?.data || {});
      messageApi.success(`Expected Closing Cash ${hide ? 'hidden' : 'shown'}`);
    } catch (err) {
      messageApi.error(err?.response?.data?.message || 'Failed to update');
    } finally {
      setTogglingHideECCId(null);
    }
  };

  const handleToggleHideEC = async (branchId, hide) => {
    setTogglingHideECId(branchId);
    try {
      const res = await setBranchHideExpectedCash(branchId, hide);
      applyOverrides(res?.data?.data || {});
      messageApi.success(`Expected Cash ${hide ? 'hidden' : 'shown'}`);
    } catch (err) {
      messageApi.error(err?.response?.data?.message || 'Failed to update');
    } finally {
      setTogglingHideECId(null);
    }
  };

  const handleToggleHideDiff = async (branchId, hide) => {
    setTogglingHideDiffId(branchId);
    try {
      const res = await setBranchHideDifference(branchId, hide);
      applyOverrides(res?.data?.data || {});
      messageApi.success(`Difference ${hide ? 'hidden' : 'shown'}`);
    } catch (err) {
      messageApi.error(err?.response?.data?.message || 'Failed to update');
    } finally {
      setTogglingHideDiffId(null);
    }
  };

  const handleToggleHideGstToggle = async (branchId, hide) => {
    setTogglingHideGstToggleId(branchId);
    try {
      const res = await setBranchHideGstToggle(branchId, hide);
      applyOverrides(res?.data?.data || {});
      messageApi.success(`GST Toggle ${hide ? 'hidden' : 'shown'}`);
    } catch (err) {
      messageApi.error(err?.response?.data?.message || 'Failed to update');
    } finally {
      setTogglingHideGstToggleId(null);
    }
  };

  const handleToggleHideProcessingFeeToggle = async (branchId, hide) => {
    setTogglingHideProcessingFeeToggleId(branchId);
    try {
      const res = await setBranchHideProcessingFeeToggle(branchId, hide);
      applyOverrides(res?.data?.data || {});
      messageApi.success(`Processing Fee Toggle ${hide ? 'hidden' : 'shown'}`);
    } catch (err) {
      messageApi.error(err?.response?.data?.message || 'Failed to update');
    } finally {
      setTogglingHideProcessingFeeToggleId(null);
    }
  };

  // ── Cash limit inline editing ─────────────────────────────────────────────
  const startEditLimit = (branchId) => {
    setEditingLimitId(branchId);
    setEditingLimitValue(cashApprovalLimits[branchId] ?? null);
  };

  const cancelEditLimit = () => {
    setEditingLimitId(null);
    setEditingLimitValue(null);
  };

  const saveLimit = async (branchId, value) => {
    setSavingLimitId(branchId);
    try {
      const res = await setBranchCashLimit(branchId, value);
      applyOverrides(res?.data?.data || {});
      messageApi.success(
        value == null
          ? 'Branch limit cleared — using global'
          : `Branch limit set to ${fmtAmount(value)}`
      );
      setEditingLimitId(null);
    } catch (err) {
      messageApi.error(err?.response?.data?.message || 'Failed to save limit');
    } finally {
      setSavingLimitId(null);
    }
  };

  // ── Pledge cash limit inline editing ──────────────────────────────────────
  const startEditPledgeLimit = (branchId) => {
    setEditingPledgeLimitId(branchId);
    setEditingPledgeLimitValue(pledgeCashApprovalLimits[branchId] ?? null);
  };

  const cancelEditPledgeLimit = () => {
    setEditingPledgeLimitId(null);
    setEditingPledgeLimitValue(null);
  };

  const savePledgeLimit = async (branchId, value) => {
    setSavingPledgeLimitId(branchId);
    try {
      const res = await setBranchPledgeCashLimit(branchId, value);
      applyOverrides(res?.data?.data || {});
      messageApi.success(
        value == null
          ? 'Branch pledge limit cleared — using global'
          : `Branch pledge limit set to ${fmtAmount(value)}`
      );
      setEditingPledgeLimitId(null);
    } catch (err) {
      messageApi.error(err?.response?.data?.message || 'Failed to save pledge limit');
    } finally {
      setSavingPledgeLimitId(null);
    }
  };

  // ── Guards ─────────────────────────────────────────────────────────────────
  if (initLoading) return <Spin style={{ display: 'block', marginTop: 80 }} />;

  if (!isHO) {
    return (
      <div style={{ padding: '80px 0', textAlign: 'center' }}>
        <LockOutlined style={{ fontSize: 48, color: '#d9d9d9', marginBottom: 16 }} />
        <Title level={5} type="secondary">Treasury Settings</Title>
        <Text type="secondary">This section is restricted to Head Office users.</Text>
      </div>
    );
  }

  // ── Branch card: collapse panel label ────────────────────────────────────
  function BranchPanelHeader({ branch }) {
    const id = branch._id;
    const activeTags = [];
    const hasCustomLimit = cashApprovalLimits[id] !== undefined;
    const hasPledgeLimit = pledgeCashApprovalLimits[id] !== undefined;
    const bankAuto = bankAutoApproveIds.includes(id);
    const bypassed = bypassedIds.includes(id);
    const pastDate = bypassPastDateIds.includes(id);
    const hideECC = hideExpectedClosingCashIds.includes(id);
    const hideEC  = hideExpectedCashIds.includes(id);
    const hideDiff = hideDifferenceIds.includes(id);
    const hideGstToggle = hideGstToggleIds.includes(id);
    const hideProcessingFeeToggle = hideProcessingFeeToggleIds.includes(id);

    if (hasCustomLimit) activeTags.push(<Tag key="cl" color="blue"   style={{ fontSize: 11 }}>Cash: {fmtAmount(cashApprovalLimits[id])}</Tag>);
    if (hasPledgeLimit) activeTags.push(<Tag key="pl" color="geekblue" style={{ fontSize: 11 }}>Pledge: {fmtAmount(pledgeCashApprovalLimits[id])}</Tag>);
    if (bankAuto)       activeTags.push(<Tag key="ba" color="green"  style={{ fontSize: 11 }}>Bank Auto</Tag>);
    if (bypassed)       activeTags.push(<Tag key="dc" color="orange" style={{ fontSize: 11 }}>Skip Day-Close</Tag>);
    if (pastDate)       activeTags.push(<Tag key="pd" color="gold"   style={{ fontSize: 11 }}>Past EOD</Tag>);
    if (hideECC)        activeTags.push(<Tag key="ecc" color="purple" style={{ fontSize: 11 }}>Hide Exp. Closing</Tag>);
    if (hideEC)         activeTags.push(<Tag key="ec"  color="purple" style={{ fontSize: 11 }}>Hide Exp. Cash</Tag>);
    if (hideDiff)       activeTags.push(<Tag key="diff" color="purple" style={{ fontSize: 11 }}>Hide Diff</Tag>);
    if (hideGstToggle)  activeTags.push(<Tag key="gst" color="purple" style={{ fontSize: 11 }}>Hide GST Toggle</Tag>);
    if (hideProcessingFeeToggle) activeTags.push(<Tag key="pf" color="purple" style={{ fontSize: 11 }}>Hide Proc. Fee</Tag>);

    return (
      <Row align="middle" gutter={12} style={{ flexWrap: 'nowrap' }}>
        <Col flex="none">
          <ShopOutlined style={{ fontSize: 16, color: '#595959' }} />
        </Col>
        <Col flex="auto">
          <Text strong style={{ fontSize: 14 }}>{branch.name}</Text>
          {branch.code && (
            <Text type="secondary" style={{ fontSize: 12, marginLeft: 6 }}>({branch.code})</Text>
          )}
        </Col>
        {activeTags.length > 0 && (
          <Col flex="none">
            <Space size={4}>{activeTags}</Space>
          </Col>
        )}
        {activeTags.length === 0 && (
          <Col flex="none">
            <Text type="secondary" style={{ fontSize: 11 }}>No overrides</Text>
          </Col>
        )}
      </Row>
    );
  }

  // ── Branch card: collapse panel body ─────────────────────────────────────
  function BranchPanelBody({ branch }) {
    const id = branch._id;
    const hasCustomLimit = cashApprovalLimits[id] !== undefined;
    const limitVal = cashApprovalLimits[id];
    const isEditingLimit = editingLimitId === id;
    const isSavingLimit = savingLimitId === id;

    const hasPledgeLimit = pledgeCashApprovalLimits[id] !== undefined;
    const pledgeLimitVal = pledgeCashApprovalLimits[id];
    const isEditingPledgeLimit = editingPledgeLimitId === id;
    const isSavingPledgeLimit = savingPledgeLimitId === id;

    const cashLimitControl = isEditingLimit ? (
      <Space size={4}>
        <InputNumber
          size="small"
          min={0}
          step={1000}
          value={editingLimitValue}
          onChange={setEditingLimitValue}
          style={{ width: 120 }}
          formatter={v => `₹ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          parser={v => v.replace(/₹\s?|(,*)/g, '')}
          autoFocus
        />
        <Tooltip title="Save">
          <Button
            type="primary"
            size="small"
            icon={<CheckOutlined />}
            loading={isSavingLimit}
            onClick={() => saveLimit(id, editingLimitValue)}
          />
        </Tooltip>
        {hasCustomLimit && (
          <Popconfirm
            title="Remove override and use global limit?"
            onConfirm={() => saveLimit(id, null)}
            okText="Reset"
            cancelText="Cancel"
          >
            <Tooltip title="Reset to global">
              <Button size="small" danger>Reset</Button>
            </Tooltip>
          </Popconfirm>
        )}
        <Button size="small" onClick={cancelEditLimit}>Cancel</Button>
      </Space>
    ) : (
      <Space size={6}>
        <Tag color={hasCustomLimit ? 'blue' : 'default'} style={{ fontSize: 12 }}>
          {hasCustomLimit ? fmtAmount(limitVal) : `Global (${fmtAmount(globalThreshold)})`}
        </Tag>
        <Tooltip title="Edit">
          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => startEditLimit(id)} />
        </Tooltip>
      </Space>
    );

    const pledgeLimitControl = isEditingPledgeLimit ? (
      <Space size={4}>
        <InputNumber
          size="small"
          min={0}
          step={1000}
          value={editingPledgeLimitValue}
          onChange={setEditingPledgeLimitValue}
          style={{ width: 120 }}
          formatter={v => `₹ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          parser={v => v.replace(/₹\s?|(,*)/g, '')}
          autoFocus
        />
        <Tooltip title="Save">
          <Button
            type="primary"
            size="small"
            icon={<CheckOutlined />}
            loading={isSavingPledgeLimit}
            onClick={() => savePledgeLimit(id, editingPledgeLimitValue)}
          />
        </Tooltip>
        {hasPledgeLimit && (
          <Popconfirm
            title="Remove override and use global pledge limit?"
            onConfirm={() => savePledgeLimit(id, null)}
            okText="Reset"
            cancelText="Cancel"
          >
            <Tooltip title="Reset to global">
              <Button size="small" danger>Reset</Button>
            </Tooltip>
          </Popconfirm>
        )}
        <Button size="small" onClick={cancelEditPledgeLimit}>Cancel</Button>
      </Space>
    ) : (
      <Space size={6}>
        <Tag color={hasPledgeLimit ? 'geekblue' : 'default'} style={{ fontSize: 12 }}>
          {hasPledgeLimit ? fmtAmount(pledgeLimitVal) : `Global (${fmtAmount(globalPledgeThreshold)})`}
        </Tag>
        <Tooltip title="Edit">
          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => startEditPledgeLimit(id)} />
        </Tooltip>
      </Space>
    );

    return (
      <div style={{ padding: '0 4px' }}>
        <SettingRow
          icon={<DollarOutlined />}
          label="Cash Auto-Approval Limit"
          description="Walk-in cash payments at or below this amount are auto-approved. Overrides the global limit for this branch."
          control={cashLimitControl}
        />
        <SettingRow
          icon={<DollarOutlined />}
          label="Pledge Cash Auto-Approval Limit"
          description="Pledge advance & settlement cash payments at or below this amount are auto-approved. Overrides the global pledge limit for this branch."
          control={pledgeLimitControl}
        />
        <SettingRow
          icon={<BankOutlined />}
          label="Auto-approve Bank Payments"
          description="Bank / UPI / cheque payments skip HO review and are approved instantly."
          control={
            <Switch
              checked={bankAutoApproveIds.includes(id)}
              loading={togglingBankId === id}
              onChange={(checked) => handleToggleBankAutoApprove(id, checked)}
              checkedChildren="On"
              unCheckedChildren="Off"
            />
          }
        />
        <SettingRow
          icon={<SafetyCertificateOutlined />}
          label="Skip Previous-Day Closure Check"
          description="Allow new transactions and EOD without closing the previous business day first."
          control={
            <Switch
              checked={bypassedIds.includes(id)}
              loading={togglingId === id}
              onChange={(checked) => handleToggleBypass(id, checked)}
              checkedChildren="On"
              unCheckedChildren="Off"
            />
          }
        />
        <SettingRow
          icon={<CalendarOutlined />}
          label="Allow Past-Date EOD"
          description="Allow EOD to be initiated for dates before today (useful for back-dated corrections). Branch-wide — for specific people instead, add them under Day Closure Override in the General tab."
          control={
            <Switch
              checked={bypassPastDateIds.includes(id)}
              loading={togglingPastDateId === id}
              onChange={(checked) => handleTogglePastDateEod(id, checked)}
              checkedChildren="On"
              unCheckedChildren="Off"
            />
          }
        />

        <Divider orientation="left" orientationMargin={0} style={{ fontSize: 11, color: '#8c8c8c', margin: '4px 0' }}>
          EOD Display
        </Divider>

        <SettingRow
          icon={<EyeInvisibleOutlined />}
          label="Show Expected Closing Cash"
          description="Display the expected closing balance in the EOD Cash Summary card."
          control={
            <Switch
              checked={!hideExpectedClosingCashIds.includes(id)}
              loading={togglingHideECCId === id}
              onChange={(checked) => handleToggleHideECC(id, !checked)}
              checkedChildren="Show"
              unCheckedChildren="Hide"
            />
          }
        />
        <SettingRow
          icon={<EyeInvisibleOutlined />}
          label="Show Expected Cash"
          description="Display the Expected Cash row in the denomination count table."
          control={
            <Switch
              checked={!hideExpectedCashIds.includes(id)}
              loading={togglingHideECId === id}
              onChange={(checked) => handleToggleHideEC(id, !checked)}
              checkedChildren="Show"
              unCheckedChildren="Hide"
            />
          }
        />
        <SettingRow
          icon={<EyeInvisibleOutlined />}
          label="Show Difference"
          description="Display the Difference row in the denomination count table."
          control={
            <Switch
              checked={!hideDifferenceIds.includes(id)}
              loading={togglingHideDiffId === id}
              onChange={(checked) => handleToggleHideDiff(id, !checked)}
              checkedChildren="Show"
              unCheckedChildren="Hide"
            />
          }
        />
        <SettingRow
          icon={<EyeInvisibleOutlined />}
          label="Show GST Toggle"
          description="Display the With GST toggle in Walk-In and Pledge Case Creation."
          control={
            <Switch
              checked={!hideGstToggleIds.includes(id)}
              loading={togglingHideGstToggleId === id}
              onChange={(checked) => handleToggleHideGstToggle(id, !checked)}
              checkedChildren="Show"
              unCheckedChildren="Hide"
            />
          }
        />
        <SettingRow
          icon={<EyeInvisibleOutlined />}
          label="Show Processing Fee"
          description="Display and calculate Processing Fee in Walk-In, Pledge Case Creation and Bills."
          control={
            <Switch
              checked={!hideProcessingFeeToggleIds.includes(id)}
              loading={togglingHideProcessingFeeToggleId === id}
              onChange={(checked) => handleToggleHideProcessingFeeToggle(id, !checked)}
              checkedChildren="Show"
              unCheckedChildren="Hide"
            />
          }
          divider={false}
        />
      </div>
    );
  }

  // ── Build collapse items ──────────────────────────────────────────────────
  const collapseItems = branches.map(branch => ({
    key: branch._id,
    label: <BranchPanelHeader branch={branch} />,
    children: <BranchPanelBody branch={branch} />,
  }));

  // ── Summary counts ────────────────────────────────────────────────────────
  const bankAutoCount = bankAutoApproveIds.length;
  const cashOverrideCount = Object.keys(cashApprovalLimits).length;
  const bypassCount = bypassedIds.length;
  const pastDateCount = bypassPastDateIds.length;
  const hideECCCount = hideExpectedClosingCashIds.length;
  const hideECCount = hideExpectedCashIds.length;
  const hideDiffCount = hideDifferenceIds.length;
  const hideGstToggleCount = hideGstToggleIds.length;
  const hideProcessingFeeToggleCount = hideProcessingFeeToggleIds.length;
  const totalOverrideCount = bankAutoCount + cashOverrideCount + bypassCount + pastDateCount + hideECCCount + hideECCount + hideDiffCount + hideGstToggleCount + hideProcessingFeeToggleCount;

  // ── Tabs ──────────────────────────────────────────────────────────────────
  const tabItems = [
    {
      key: 'general',
      label: <Space><SettingOutlined />General</Space>,
      children: (
        <Spin spinning={generalLoading}>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Form form={generalForm} layout="vertical" onFinish={handleSaveGeneral}>
                <Card title="Payment Approvals" size="small">
                  <Form.Item
                    name="cashApprovalThreshold"
                    label="Global Cash Auto-Approval Limit (₹)"
                    tooltip="Cash payments at or below this amount are auto-approved without HO review. Applied to all branches unless a branch-level override is set."
                    rules={[{ required: true, type: 'number', min: 0, message: 'Enter a valid amount' }]}
                  >
                    <InputNumber
                      min={0}
                      step={1000}
                      style={{ width: '100%' }}
                      formatter={v => `₹ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={v => v.replace(/₹\s?|(,*)/g, '')}
                    />
                  </Form.Item>
                  <Form.Item
                    name="pledgeCashApprovalThreshold"
                    label="Global Pledge Cash Auto-Approval Limit (₹)"
                    tooltip="Pledge advance & settlement cash payments at or below this amount are auto-approved without HO review. Defaults to ₹0 — meaning every pledge payment requires HO approval until you raise it."
                    rules={[{ required: true, type: 'number', min: 0, message: 'Enter a valid amount' }]}
                  >
                    <InputNumber
                      min={0}
                      step={1000}
                      style={{ width: '100%' }}
                      formatter={v => `₹ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      parser={v => v.replace(/₹\s?|(,*)/g, '')}
                    />
                  </Form.Item>
                  <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 16 }}>
                    Pledge cash is governed by its own limit above (separate from walk-in). Cash above the
                    limit, and all bank/UPI/cheque payments, still require HO approval. Per-branch limits
                    can be set in the Branch Overrides tab.
                  </Paragraph>
                  <Form.Item
                    name="gstToggleEnabled"
                    label="Enable Global GST Toggle"
                    tooltip="If enabled, branches can toggle GST on/off during Case Creation unless overridden."
                    valuePropName="checked"
                  >
                    <Switch checkedChildren="Enabled" unCheckedChildren="Disabled" />
                  </Form.Item>
                  <Form.Item
                    name="processingFeeToggleEnabled"
                    label="Enable Global Processing Fee"
                    tooltip="If enabled, Processing Fee can be calculated in Cases and Bills unless overridden."
                    valuePropName="checked"
                  >
                    <Switch checkedChildren="Enabled" unCheckedChildren="Disabled" />
                  </Form.Item>
                </Card>

                <Card title="Bill Settings" size="small" style={{ marginTop: 16 }}>
                  <Form.Item
                    name="billHideGrossWeight"
                    label="Hide Gross Weight (g)"
                    valuePropName="checked"
                    tooltip="Hide Gross Weight column in all PDFs (Walk-In, Pledge, Bill)"
                  >
                    <Switch checkedChildren="Hidden" unCheckedChildren="Visible" />
                  </Form.Item>
                  <Form.Item
                    name="billHidePurity"
                    label="Hide Purity %"
                    valuePropName="checked"
                    tooltip="Hide Purity % column in all PDFs"
                  >
                    <Switch checkedChildren="Hidden" unCheckedChildren="Visible" />
                  </Form.Item>
                  <Form.Item
                    name="billHideDD"
                    label="Hide D.D"
                    valuePropName="checked"
                    tooltip="Hide Dust Deduction column in all PDFs"
                  >
                    <Switch checkedChildren="Hidden" unCheckedChildren="Visible" />
                  </Form.Item>
                  <Form.Item
                    name="billHideNetWeight"
                    label="Hide Net Wt"
                    valuePropName="checked"
                    tooltip="Hide Net Weight column in all PDFs"
                  >
                    <Switch checkedChildren="Hidden" unCheckedChildren="Visible" />
                  </Form.Item>
                </Card>

                <Card
                  title={<Space><UnlockOutlined />Day Closure Override</Space>}
                  size="small"
                  style={{ marginTop: 16 }}
                >
                  <Form.Item
                    name="dayClosureOverrideUsers"
                    label="Users allowed to force-close stuck previous days & run past-date EOD"
                    tooltip="Only these users can override the “previous business day is still OPEN” block. On the End of Day page they can (a) enter denomination counts and directly close each unclosed previous day — any cash difference is recorded — which unblocks opening today, and (b) initiate EOD for a past date (back-dated corrections) regardless of the per-branch Allow Past-Date EOD toggle."
                  >
                    <Select
                      mode="multiple"
                      allowClear
                      loading={usersLoading}
                      placeholder="Select users who can override day closure"
                      optionFilterProp="label"
                      maxTagCount="responsive"
                      options={users.map(u => ({
                        value: u._id,
                        label: `${u.name || u.email || u._id}${u.role ? ` · ${u.role}` : ''}${u.branch?.name ? ` · ${u.branch.name}` : ''}`,
                      }))}
                    />
                  </Form.Item>
                  <Paragraph type="secondary" style={{ fontSize: 12, margin: 0 }}>
                    Leave empty to allow no one. These users close previous days with a
                    proper denomination count and any discrepancy is recorded for audit —
                    unlike the per-branch “Skip Previous-Day Closure” toggle, which merely
                    ignores the check and leaves old days open.
                  </Paragraph>
                </Card>

                <div style={{ marginTop: 16 }}>
                  <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={generalSaving}>
                    Save All Settings
                  </Button>
                </div>
              </Form>
            </Col>
          </Row>
        </Spin>
      ),
    },
    {
      key: 'branch-overrides',
      label: (
        <Space>
          <BranchesOutlined />
          Branch Overrides
          {totalOverrideCount > 0 && (
            <Badge count={totalOverrideCount} size="small" style={{ backgroundColor: '#fa8c16' }} />
          )}
        </Space>
      ),
      children: (
        <Spin spinning={branchesLoading}>
          <Alert
            type="warning"
            showIcon
            message="Use overrides only as a temporary measure. Leaving them on indefinitely risks reconciliation gaps."
            style={{ marginBottom: 16 }}
          />

          {/* Summary strip */}
          {totalOverrideCount > 0 && (
            <Space wrap style={{ marginBottom: 16 }}>
              {cashOverrideCount > 0 && <Tag color="blue">{cashOverrideCount} Custom Cash Limit</Tag>}
              {bankAutoCount > 0 && <Tag color="green">{bankAutoCount} Bank Auto-Approve</Tag>}
              {bypassCount > 0 && <Tag color="orange">{bypassCount} Skip Day-Close</Tag>}
              {pastDateCount > 0 && <Tag color="gold">{pastDateCount} Past-date EOD</Tag>}
              {(hideECCCount + hideECCount + hideDiffCount + hideGstToggleCount + hideProcessingFeeToggleCount) > 0 && (
                <Tag color="purple">{hideECCCount + hideECCount + hideDiffCount + hideGstToggleCount + hideProcessingFeeToggleCount} Hidden Features</Tag>
              )}
            </Space>
          )}

          {branches.length === 0 && !branchesLoading ? (
            <Text type="secondary">No branches found.</Text>
          ) : (
            <Collapse
              accordion={false}
              items={collapseItems}
              style={{ background: 'transparent' }}
              expandIconPosition="end"
            />
          )}
        </Spin>
      ),
    },
  ];

  return (
    <div style={{ padding: '16px 0' }}>
      {contextHolder}

      <Row justify="space-between" align="middle" style={{ marginBottom: 20 }}>
        <Col>
          <Title level={5} style={{ margin: 0 }}>Treasury Settings</Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Head Office configuration for treasury operations
          </Text>
        </Col>
      </Row>

      <Tabs items={tabItems} defaultActiveKey="general" />
    </div>
  );
}
